import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import useGameAndVersion from "../shared/hooks/useGameAndVersion";
import { LoadingScreen } from "./components/LoadingScreen";

/** The first of the browser's languages that the tutorial comes in, matching on language alone as a fallback */
function pickLocale(locales: string[] | undefined): string | undefined {
  if (!locales?.length) {
    return undefined;
  }
  const preferred = navigator.languages ?? [navigator.language];
  for (const lang of preferred) {
    const exact = locales.find((l) => l.toLowerCase() === lang.toLowerCase());
    if (exact) {
      return exact;
    }
    const base = lang.split("-")[0].toLowerCase();
    const partial = locales.find((l) => l.split("-")[0].toLowerCase() === base);
    if (partial) {
      return partial;
    }
  }
  return undefined;
}

export default function DemoPlayer() {
  const { gameName, version } = useParams();
  const [searchParams] = useSearchParams();
  const demoId = searchParams.get("demo") ?? "demo";

  const [loaded, setLoaded] = useState(false);
  const [module, setModule] = useState<any>();
  const containerRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);

  const { gameVersion } = useGameAndVersion(gameName, version ?? null);

  // Load game module
  useEffect(() => {
    if (!gameVersion) {
      setModule(undefined);
      return;
    }

    import(/* @vite-ignore */ /* webpackIgnore: true */ gameVersion.webEntry)
      .then((mod: any) => {
        setModule(mod);
      })
      .catch((err: any) => {
        console.error("Failed to load demo module:", err);
      });
  }, [gameVersion]);

  // Mount the demo viewer
  useEffect(() => {
    if (!module || mountedRef.current) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    mountedRef.current = true;
    container.style.display = "block";

    setTimeout(() => setLoaded(true), 250);

    const demo = gameVersion?.demos?.find((d) => d.id === demoId);
    module.createDemoMatchViewer(container, {
      demo: demo?.id,
      locale: searchParams.get("locale") ?? pickLocale(demo?.locales),
      onBack: () => {
        window.location.href = version
          ? `/game/${gameName}/version/${version}`
          : `/game/${gameName}`;
      },
    });
  }, [module]);

  return (
    <>
      {!loaded && (
        <LoadingScreen
          backgroundColor={gameVersion?.backgroundColor}
          foregroundColor={gameVersion?.foregroundColor}
        />
      )}

      <div
        id="player"
        ref={containerRef}
        style={{
          display: "none",
          width: "100%",
          height: "100%",
        }}
      />
    </>
  );
}
